import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import {
  CARRIER_ADMITTED_STATUS_LABELS,
  CARRIER_COI_SPEED_LABELS,
  CarrierAdmittedStatus,
  CarrierCoiSpeed,
  type CarrierProfile,
  CarrierRatingValue,
} from '@/models/insuranceCarrier'

export type EditableProfile = CarrierProfile

export const EMPTY_PROFILE: EditableProfile = {
  publicNote: '',
  rating: '',
  coiSpeed: '',
  admittedStatus: '',
  financingOptions: '',
  allowsIndividualCoveragePurchase: false,
  claimsHasMultipleWaysToFile: false,
  claimsWaysToFile: '',
  claimsHas247Support: false,
  claimsSupportAvailability: '',
  claimsHasPayoutInformation: false,
  claimsWaysToGetPaid: '',
  claimsPayoutSpeed: '',
}

// Sentinel for the Select's "nothing on file" option — Radix treats an empty string
// value as "no selection" and refuses to render an item with one.
const NONE = 'none'

interface CarrierProfileFieldsProps {
  value: EditableProfile
  onChange: (patch: Partial<EditableProfile>) => void
  /** Leaves out insurance type, rating and COI speed, which a pet carrier doesn't have. */
  pet?: boolean
}

/**
 * What the quotes page shows a customer about a carrier. Each claims capability pairs
 * the checkbox its filter tests against with the prose the card prints — the two are
 * separate on purpose, so a rephrased sentence can't silently drop a carrier out of a
 * filter.
 */
export function CarrierProfileFields({ value, onChange, pet }: CarrierProfileFieldsProps) {
  const claimsRows: { key: keyof EditableProfile; label: string; placeholder: string }[] = [
    {
      key: 'claimsWaysToFile',
      label: 'Ways to file',
      placeholder: 'File online, through the app, or by phone.',
    },
    {
      key: 'claimsSupportAvailability',
      label: 'Availability',
      placeholder: '24/7 phone support.',
    },
    { key: 'claimsPayoutSpeed', label: 'Payout speed', placeholder: 'As little as 48 hours.' },
    {
      key: 'claimsWaysToGetPaid',
      label: 'Ways to get paid',
      placeholder: 'Digital payment or mail.',
    },
  ]

  const claimsFlags: { key: keyof EditableProfile; label: string }[] = [
    { key: 'claimsHasMultipleWaysToFile', label: 'Multiple ways to file' },
    { key: 'claimsHas247Support', label: '24/7 support' },
    { key: 'claimsHasPayoutInformation', label: 'Has payout information' },
  ]

  return (
    <>
      <div className="border-brand-200 bg-brand-100/40 flex flex-col gap-2 rounded-lg border p-3">
        <div className="flex items-center gap-2">
          <Label htmlFor="carrier-public-note" className="font-bold">
            Carrier notes
          </Label>
          <Badge variant="secondary">Customers see this</Badge>
        </div>
        <Textarea
          id="carrier-public-note"
          value={value.publicNote}
          onChange={(e) => onChange({ publicNote: e.target.value })}
          placeholder="ELD / dashcam: Required pre-bind — confirm before start"
        />
        <p className="text-muted-foreground text-xs">
          Shown under &ldquo;Carrier notes&rdquo; in Carrier details, on every quote/indication from this
          carrier. This is separate from the per-quote broker note (edit that in the internal Console
          Insurance tab). For anything customers shouldn&apos;t read, use Internal notes above.
        </p>
      </div>

      {!pet && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div className="flex flex-col gap-2">
            <Label htmlFor="carrier-admitted-status">Insurance type</Label>
            <Select
              value={value.admittedStatus || NONE}
              onValueChange={(next) =>
                onChange({ admittedStatus: next === NONE ? '' : (next as CarrierAdmittedStatus) })
              }
            >
              <SelectTrigger id="carrier-admitted-status">
                <SelectValue placeholder="Unknown" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>Unknown</SelectItem>
                {Object.values(CarrierAdmittedStatus).map((status) => (
                  <SelectItem key={status} value={status}>
                    {CARRIER_ADMITTED_STATUS_LABELS[status]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-muted-foreground text-xs">
              Shown to customers as &ldquo;Insurance type&rdquo;. Anything on Excess and Surplus paper is
              non-admitted.
            </p>
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="carrier-rating">Rating</Label>
            <Select
              value={value.rating || NONE}
              onValueChange={(next) =>
                onChange({ rating: next === NONE ? '' : (next as CarrierRatingValue) })
              }
            >
              <SelectTrigger id="carrier-rating">
                <SelectValue placeholder="No rating" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>No rating</SelectItem>
                {Object.values(CarrierRatingValue).map((rating) => (
                  <SelectItem key={rating} value={rating}>
                    {rating}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="carrier-coi-speed">COI speed</Label>
            <Select
              value={value.coiSpeed || NONE}
              onValueChange={(next) => onChange({ coiSpeed: next === NONE ? '' : (next as CarrierCoiSpeed) })}
            >
              <SelectTrigger id="carrier-coi-speed">
                <SelectValue placeholder="Unknown" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>Unknown</SelectItem>
                {Object.values(CarrierCoiSpeed).map((speed) => (
                  <SelectItem key={speed} value={speed}>
                    {CARRIER_COI_SPEED_LABELS[speed]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      <div className="flex flex-col gap-2">
        <Label htmlFor="carrier-financing">Financing options</Label>
        <Textarea
          id="carrier-financing"
          value={value.financingOptions}
          onChange={(e) => onChange({ financingOptions: e.target.value })}
          placeholder="Split your premium into 10 monthly payments at 0% APR."
        />
        <p className="text-muted-foreground text-xs">
          Leave empty when the carrier offers no financing — the quote card reads empty as &ldquo;not
          available&rdquo;.
        </p>
      </div>

      <div className="flex flex-col gap-2">
        <label className="flex cursor-pointer items-center gap-2 text-sm font-bold">
          <Checkbox
            checked={value.allowsIndividualCoveragePurchase}
            onCheckedChange={(checked) => onChange({ allowsIndividualCoveragePurchase: checked === true })}
          />
          Coverages can be bought individually
        </label>
        <p className="text-muted-foreground text-xs">
          Gives every coverage on this carrier&apos;s quote cards its own &ldquo;Add to cart&rdquo;. Leave off
          when the coverages are a bundle — the customer has to take all of them to get that price.
        </p>
      </div>

      <fieldset className="flex flex-col gap-3">
        <legend className="text-sm font-bold">Claims capabilities</legend>
        <div className="flex flex-wrap gap-4">
          {claimsFlags.map((flag) => (
            <label key={flag.key} className="flex cursor-pointer items-center gap-2 text-sm">
              <Checkbox
                checked={value[flag.key] as boolean}
                onCheckedChange={(checked) => onChange({ [flag.key]: checked === true })}
              />
              {flag.label}
            </label>
          ))}
        </div>
        {claimsRows.map((row) => (
          <div key={row.key} className="flex flex-col gap-2">
            <Label htmlFor={`carrier-${row.key}`}>{row.label}</Label>
            <Input
              id={`carrier-${row.key}`}
              value={value[row.key] as string}
              onChange={(e) => onChange({ [row.key]: e.target.value })}
              placeholder={row.placeholder}
            />
          </div>
        ))}
      </fieldset>
    </>
  )
}
